import axios, { AxiosError, AxiosResponse } from 'axios';
import moment from 'moment-timezone';
import { Filters, FilterValue, IdType, SortingRule } from 'react-table';

import { AllResultsRequestBody, IDeleteFilters, RequestBody } from 'models/http/common';
import { BLANK_FILTER_ID, MTableQueryFilters, RowData } from 'views/MTable/Table.model';

const rootAxios = axios.create({
  baseURL: import.meta.env.VITE_API_ENDPOINT,
  timeout: import.meta.env.VITE_SERVER_REQUEST_TIMEOUT,
});

rootAxios.interceptors.response.use(
  (response: AxiosResponse): AxiosResponse => response,
  (error: AxiosError<Record<string, unknown>>) => {
    if (!axios.isCancel(error)) {
      const errorMessage = error?.response?.data?.error || 'Oops! An unexpected error occurred.';
      const errorCode = error?.response?.data?.code;
      const httpCode = error?.response?.status;
      const fields = error?.response?.data?.fields;

      return Promise.reject({ message: errorMessage, errorCode, httpCode, fields });
    }

    return Promise.resolve();
  },
);

export const serverAxios = rootAxios;

export const pbAxios = axios.create({
  baseURL: import.meta.env.VITE_PANDORA_BOT_URL,
});

/** Never assume error type - use optional chaining, e.g.: (error as IPossibleAxiosError)?.errorCode */
export interface IPossibleAxiosError {
  message?: string;
  errorCode?: number;
  httpCode?: number;
  fields?: Record<string, unknown>;
}

export const prepareQPFromFilters = (filters: Record<string, unknown>): URLSearchParams => {
  const queryParams = new URLSearchParams();

  Object.entries(filters).forEach(([key, value]: [string, number | string | unknown]) => {
    if (Array.isArray(value)) {
      value.forEach((arrValue: unknown) => queryParams.append(`${key}[]`, String(arrValue)));
    } else if (value !== undefined) {
      queryParams.append(key, String(value));
    }
  });

  return queryParams;
};

/** TODO: we need to find better name for this function, see usage in user.http.ts */
export const prepareFiltersOfSelection = <Filters extends IDeleteFilters>(
  filters: number[] | Filters,
): { ids: number[]; useFilters: false } | { filters: Filters; useFilters: true } => {
  return Array.isArray(filters) ? { ids: filters, useFilters: false } : { filters, useFilters: true };
};

export const prepareTableQuery = <T extends RowData, QueryFilters extends MTableQueryFilters>(filters: Filters<T>): QueryFilters => {
  const preparedFilters = filters.reduce((queryAcc: QueryFilters, filter: { id: IdType<T>; value: FilterValue }) => {
    // Array of values
    if (Array.isArray(filter.value)) {
      // Date Range
      if (filter.value.length === 2) {
        const dateQuery: { fromDate?: number; toDate?: number } = {};

        if (moment.isMoment(filter.value[0])) {
          dateQuery.fromDate = filter.value[0].unix();
        }

        if (moment.isMoment(filter.value[1])) {
          dateQuery.toDate = filter.value[1].unix();
        }

        if (Object.values(dateQuery).length > 0) {
          return {
            ...queryAcc,
            ...dateQuery,
          };
        }
      }

      // Array of values (but not date range)
      if (filter.value.length > 0) {
        if (filter.value) {
          const values = filter.value.map((value) => {
            const filterValue = value.id || value;

            return filterValue === BLANK_FILTER_ID ? null : filterValue;
          });

          return {
            ...queryAcc,
            [filter.id]: values,
          };
        }
      }

      return queryAcc;
    }

    // Date
    if (moment.isMoment(filter.value)) {
      return {
        ...queryAcc,
        [filter.id]: filter.value.unix(),
      };
    }

    if (typeof filter.value === 'string') {
      return {
        ...queryAcc,
        [filter.id]: filter.value,
      };
    }

    return queryAcc;
  }, {} as QueryFilters);

  return { ...preparedFilters };
};

export const prepareTableRequestBody = <T extends RowData, QueryFilters extends MTableQueryFilters>(
  filters: Filters<T>,
  sortingRules: SortingRule<T>[],
  page: number,
  limit: number,
  search?: string,
  enableLocationTimezone?: boolean,
): RequestBody<QueryFilters> => {
  const sortItem = sortingRules[0];

  const body: RequestBody<QueryFilters> = {
    page,
    filters: prepareTableQuery<T, QueryFilters>(filters),
    limit,
    search,
    enableLocationTimezone,
  };

  if (sortItem) {
    body.sort = `${sortItem.id}:${sortItem.desc ? 'desc' : 'asc'}`;
  }

  return body;
};

/**
 * Utility that MTable uses to expose the request object for all results (across all pages), useful for custom
 * external operations that may be implemented outside of the scope of MTable.
 */
export const prepareAllResultsRequestBody = <T extends RowData, QueryFilters extends MTableQueryFilters>(
  filters: Filters<T>,
  sortingRules: SortingRule<T>[] | null,
  search: string | null,
): AllResultsRequestBody<QueryFilters> => {
  const body: AllResultsRequestBody<QueryFilters> = {
    filters: prepareTableQuery<T, QueryFilters>(filters),
  };

  if (sortingRules?.length) {
    body.sort = `${sortingRules[0].id}:${sortingRules[0].desc ? 'desc' : 'asc'}`;
  }

  if (search) {
    body.search = search;
  }

  return body;
};
